// apps/vendor/src/components/LanguageSelector.tsx
//
// Phase 7 Wave 18 Task 79 (Amendment #1) — vendor-local LanguageSelector
// that wires the TopBar dropdown into the real i18next instance shipped by
// Task 76 (`apps/vendor/src/lib/i18n`).
//
// Before this task the TopBar rendered `<LanguageSelector />` imported from
// `@printway/ui`, which is a visual-only placeholder (defaults to EN, no
// onChange wiring — see packages/ui/src/components/TopBarRightSlot.tsx).
// Clicking it did nothing — phantom-feature flagged by.
//
// This component:
//   - Sources language list + change function from `lib/i18n` (Task 76).
//   - Reads the active locale from `useTranslation()` so it re-renders on
//     locale change (covers I18nProvider auto-sync from `user.language`).
//   - Falls back to `useAuth().user.language` when i18next.language hasn't
//     hydrated yet (first paint).
//   - Persists via the i18next LanguageDetector (cookie + localStorage —
//     `vendor.lang` cookie, see config.ts). No manual PATCH here; the
//     PreferencesPage form handles server-side persistence.
//   - Optimistically refreshes `useAuth().user` via `fetchMe()` after a
//     successful PATCH (PreferencesPage owns the PATCH; this component
//     intentionally stays client-side only — minimal coupling).
//
// vg-binding: PLAN:task-79
// vg-binding: P7.D-134
// vg-binding: P7.D-155
// vg-binding: AMENDMENT-1

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from 'react';

import { useAuth } from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import {
  SUPPORTED_LANGUAGES,
  changeLanguage,
  useTranslation,
  type SupportedLanguage,
} from '../lib/i18n/index.js';

/**
 * Persist the picked language to backend so it survives reload + cross-tab
 * sync. Mirrors PreferencesPage form submit which PATCHes both endpoints.
 *
 * Best-effort: failures are silently ignored — UI already flipped via
 * i18next.changeLanguage(); a network blip shouldn't revert the client.
 * The next /auth/me hydrate will reconcile.
 *
 * UAT B3 (2026-05-20): pre-fix LanguageSelector was session-cookie-only;
 * I18nProvider.useEffect re-synced user.language on every page mount,
 * resetting the click selection. Now selector mirrors PreferencesPage:
 * PATCH server, refresh useAuth via fetchMe.
 */
async function persistLanguage(code: SupportedLanguage): Promise<void> {
  // PATCH /api/v1/auth/me/preferences validates language with the lowercase
  // enum `['vi','en','fr']` (apps/api/.../me.routes.ts:584). Sending the
  // SupportedLanguage uppercase form `'VI'|'EN'|'FR'` returned 400 and the
  // server never persisted user.language — on next F5, I18nProvider's
  // useEffect read the stale DB value and overrode the cookie-restored
  // locale, making the selector look broken.
  const lowercase = code.toLowerCase() as 'vi' | 'en' | 'fr';
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const client = apiClient() as any;
    await Promise.all([
      typeof client.PATCH === 'function'
        ? client.PATCH('/api/v1/auth/me/preferences', {
            body: { language: lowercase, locale: lowercase },
          })
        : Promise.resolve(),
      typeof client.PATCH === 'function'
        ? client.PATCH('/api/v1/auth/me', { body: { language: lowercase } })
        : Promise.resolve(),
    ]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[LanguageSelector] persistLanguage failed', err);
  }
}

// Mirrors the @printway/ui exclusive-dropdown event so opening the language
// menu collapses sibling topbar dropdowns (NotificationBell / AccountDropdown
// / HelpDropdown). Same event name keeps cross-component coordination.
const TOPBAR_DROPDOWN_EVENT = 'topbar:dropdown:open';
let dropdownIdSeq = 0;

function useExclusiveDropdown(): [boolean, () => void, () => void] {
  const [open, setOpen] = useState(false);
  const idRef = useRef<string>('');
  if (idRef.current === '') {
    dropdownIdSeq += 1;
    idRef.current = `vendor-lang-${dropdownIdSeq}`;
  }
  useEffect(() => {
    function onOpen(ev: Event): void {
      const detail = (ev as CustomEvent<{ id: string }>).detail;
      if (detail && detail.id !== idRef.current) setOpen(false);
    }
    function onDocClick(ev: MouseEvent): void {
      // Click-outside-to-close — defer until next tick so the trigger's own
      // toggle handler fires first.
      if (!open) return;
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-vendor-lang-root="true"]')) return;
      setOpen(false);
    }
    // dbg-20260911-093626 — Escape and focus-out, to match the shared topbar
    // dropdowns. This copy of the hook had outside-click but neither of the
    // other two, so the language menu could be dismissed with the mouse and
    // not with the keyboard. Measured: Escape left it open.
    function onKeyDown(ev: KeyboardEvent): void {
      if (open && ev.key === 'Escape') setOpen(false);
    }
    function onFocusIn(ev: FocusEvent): void {
      if (!open) return;
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-vendor-lang-root="true"]')) return;
      setOpen(false);
    }
    window.addEventListener(TOPBAR_DROPDOWN_EVENT, onOpen);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener(TOPBAR_DROPDOWN_EVENT, onOpen);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open]);
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        window.dispatchEvent(
          new CustomEvent(TOPBAR_DROPDOWN_EVENT, { detail: { id: idRef.current } }),
        );
      }
      return next;
    });
  }, []);
  const close = useCallback(() => setOpen(false), []);
  return [open, toggle, close];
}

/** Map i18next locale ('vi'|'en'|'fr') → user.language ('VI'|'EN'|'FR'). */
function localeToUser(loc: string | undefined): SupportedLanguage {
  if (loc === 'en' || loc === 'EN') return 'EN';
  if (loc === 'fr' || loc === 'FR') return 'FR';
  return 'VI';
}

export function LanguageSelector(): JSX.Element {
  const { i18n, t } = useTranslation('common');
  const user = useAuth((s) => s.user);
  const [open, toggle, close] = useExclusiveDropdown();

  // Priority: live i18next locale → user.language → default VI. This means
  // when the LanguageDetector flips the locale (cookie/localStorage hit) the
  // TopBar reflects it without waiting for /auth/me.
  const current: SupportedLanguage =
    localeToUser(i18n.language) ?? localeToUser(user?.language ?? undefined);

  const fetchMe = useAuth((s) => s.fetchMe);

  const handleSelect = (code: SupportedLanguage): void => {
    // Flip UI immediately (don't wait for network).
    void changeLanguage(code);
    close();
    // UAT B3 (2026-05-20): also persist to backend so reload preserves
    // the choice (was previously cookie-session-only — I18nProvider's
    // useEffect re-synced user.language on every page mount).
    //
    // UAT B6 (2026-05-20): when LanguageSelector is mounted on pre-auth
    // surfaces (login/register/forgot-password), `user` is null. Skip the
    // PATCH (would 401) — the LanguageDetector cookie still persists the
    // pick across reload for anonymous sessions.
    if (!user) return;
    void (async () => {
      await persistLanguage(code);
      if (typeof fetchMe === 'function') {
        try {
          await fetchMe();
        } catch {
          /* ignore — UI already flipped, next mount will reconcile */
        }
      }
    })();
  };

  // i18next change event → forced re-render. `useTranslation` already
  // subscribes via context, but on first mount the language detector might
  // race with the user-store hydration; this effect keeps `current` synced.
  useEffect(() => {
    function onChange(): void {
      // No-op state setter to bust the memo since `i18n.language` is read
      // directly above; React rerenders on the parent useTranslation.
    }
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [i18n]);

  return (
    <div className="relative" data-vendor-lang-root="true">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('topbar.selectLanguage')}
        data-testid="topbar-language-selector"
        className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pw-primary-strong"
      >
        <LanguagesIcon className="h-4 w-4" aria-hidden />
        <span className="hidden md:inline">{current}</span>
        <Caret />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-44 overflow-hidden rounded-lg border border-pw-border bg-pw-bg shadow-lg"
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current;
            return (
              <button
                key={lang.code}
                type="button"
                role="menuitem"
                data-testid={`topbar-language-option-${lang.code}`}
                onClick={() => handleSelect(lang.code)}
                className={
                  active
                    ? 'flex w-full items-center justify-between gap-2 bg-pw-surface px-3 py-2 text-left text-sm text-pw-text'
                    : 'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text'
                }
              >
                <span className="inline-flex items-center gap-2">
                  <span className="text-xs font-semibold tracking-wide">{lang.flag}</span>
                  <span>{lang.label}</span>
                </span>
                {active ? <CheckIcon className="h-3.5 w-3.5 text-pw-primary-strong" aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline icons — keep this file self-contained (no extra dep on lucide-react).
// ---------------------------------------------------------------------------

function LanguagesIcon(props: { className?: string; 'aria-hidden'?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
      aria-hidden={props['aria-hidden']}
    >
      <path d="m5 8 6 6" />
      <path d="m4 14 6-6 2-3" />
      <path d="M2 5h12" />
      <path d="M7 2h1" />
      <path d="m22 22-5-10-5 10" />
      <path d="M14 18h6" />
    </svg>
  );
}

function CheckIcon(props: { className?: string; 'aria-hidden'?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
      aria-hidden={props['aria-hidden']}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Caret(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3 w-3"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export default LanguageSelector;
